import { h, card, apiClient, formatEur, pill, btn, toast } from './_common.js';
import { renderSecExplorer } from './_sec.js';

// ============================================================
// INVERSIONES — Trade Republic + análisis SEC
// 4 tabs: Cartera · Watchlist · Análisis · Plan de ahorro
// ============================================================

const SCORE_STYLES = {
  bullish:  { bg: 'bg-green-500/15',  text: 'text-green-300',  label: '🟢 BULLISH' },
  bearish:  { bg: 'bg-red-500/15',    text: 'text-red-300',    label: '🔴 BEARISH' },
  warning:  { bg: 'bg-orange-500/15', text: 'text-orange-300', label: '⚠️ WARNING' },
  neutral:  { bg: 'bg-base-700/40',   text: 'text-ink-300',    label: '⚪ NEUTRAL' },
};

function fmtBigUsd(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(2) + 'B$';
  if (abs >= 1e6) return (n / 1e6).toFixed(2) + 'M$';
  if (abs >= 1e3) return (n / 1e3).toFixed(2) + 'k$';
  return n.toFixed(2) + '$';
}

function fmtPct(n) {
  if (n == null) return '—';
  const s = n > 0 ? '+' : '';
  return s + n.toFixed(2) + '%';
}

function scoreBadge(score) {
  const s = SCORE_STYLES[score] || SCORE_STYLES.neutral;
  return h('span', { class: `${s.bg} ${s.text} text-[11px] font-bold px-2 py-1 rounded-full` }, s.label);
}

export async function renderInversiones(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' });
  root.appendChild(wrap);

  // Cabecera
  wrap.appendChild(h('div', { class: 'flex items-center justify-between mb-4 flex-wrap gap-2' }, [
    h('div', {}, [
      h('h2', { class: 'font-display text-3xl font-bold' }, '📈 Inversiones'),
      h('p', { class: 'text-ink-300 text-sm' }, 'Trade Republic + análisis SEC. Cartera, watchlist y alertas.'),
    ]),
  ]));

  // Tabs
  const tabsBar = h('div', { class: 'flex gap-1 border-b border-base-700 mb-4 overflow-x-auto' });
  const tabContent = h('div');
  wrap.appendChild(tabsBar);
  wrap.appendChild(tabContent);

  const tabs = [
    { id: 'cartera',   label: '💼 Mi cartera',      fn: renderTabCartera },
    { id: 'watchlist', label: '👁️ Watchlist',       fn: renderTabWatchlist },
    { id: 'analisis',  label: '🔍 Análisis SEC',    fn: renderTabAnalisis },
    { id: 'earnings',  label: '📅 Earnings',        fn: renderTabEarnings },
    { id: 'altassets', label: '🥇 Oro & Alt',       fn: renderTabAltAssets },
    { id: 'plan',      label: '🔁 Plan de ahorro',  fn: renderTabPlan },
    { id: 'alertas',   label: '🔔 Alertas',         fn: renderTabAlertas },
  ];

  const setActive = async (id) => {
    [...tabsBar.children].forEach((c, i) => {
      const active = tabs[i].id === id;
      c.className = 'px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap ' +
        (active ? 'border-gold-400 text-gold-300' : 'border-transparent text-ink-300 hover:text-ink-100');
    });
    tabContent.innerHTML = '<div class="text-ink-400 text-sm py-8 text-center">Cargando…</div>';
    try {
      const t = tabs.find((x) => x.id === id);
      tabContent.innerHTML = '';
      await t.fn(tabContent);
    } catch (e) {
      tabContent.innerHTML = '';
      tabContent.appendChild(h('p', { class: 'text-red-400 text-sm' }, 'Error: ' + (e.message || e)));
    }
  };

  for (const t of tabs) {
    const btn = h('button', { class: 'px-3 py-2 text-sm font-medium border-b-2 border-transparent text-ink-300 hover:text-ink-100 whitespace-nowrap' }, t.label);
    btn.addEventListener('click', () => setActive(t.id));
    tabsBar.appendChild(btn);
  }
  setActive('cartera');
}

// ============================================================
// TAB 1 — CARTERA
// ============================================================
async function renderTabCartera(content) {
  const data = await apiClient.get('/api/v1/investments/cartera');
  const { holdings = [], totals = {} } = data;

  // KPIs
  const kpis = [
    { label: 'Coste base',         value: formatEur(totals.costBasisEur || 0),     color: 'text-ink-200' },
    { label: 'Valor actual',       value: formatEur(totals.currentValueEur || 0),  color: 'text-gold-300' },
    { label: 'P&L no realizada',   value: formatEur(totals.unrealizedPnlEur || 0) + ' (' + fmtPct(totals.unrealizedPnlPct) + ')', color: (totals.unrealizedPnlEur || 0) >= 0 ? 'text-green-300' : 'text-red-300' },
    { label: 'P&L realizada',      value: formatEur(totals.realizedPnlEur || 0),   color: (totals.realizedPnlEur || 0) >= 0 ? 'text-green-300' : 'text-red-300' },
    { label: 'Posiciones abiertas', value: String(totals.positionsCount || 0),     color: 'text-ink-200' },
  ];

  content.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-2 mb-4' },
    kpis.map((k) => h('div', { class: 'bg-base-900/60 border border-base-700 rounded-lg px-3 py-2' }, [
      h('div', { class: 'text-[10px] text-ink-400 uppercase tracking-wide' }, k.label),
      h('div', { class: `text-lg font-bold ${k.color}` }, k.value),
    ]))
  ));

  // Botón nueva transacción
  content.appendChild(h('div', { class: 'mb-3' }, [
    btn('+ Registrar compra/venta', () => openTxModal(() => renderTabCartera(content)), { variant: 'primary' }),
  ]));

  // Comparativa S&P 500 + export CSV
  const compareBox = h('div', { class: 'flex flex-wrap items-center gap-2 mb-3 bg-base-900/40 border border-base-700 rounded-lg p-3' }, [
    h('span', { class: 'text-sm text-ink-300 flex-1' }, '⚖️ Tu cartera vs S&P 500'),
    h('span', { class: 'text-xs text-ink-400', id: 'vs-sp500-verdict' }, 'Calculando…'),
    h('a', { href: '/api/v1/investments/transactions.csv', target: '_blank', class: 'bg-base-800 hover:bg-base-700 text-xs px-3 py-1.5 rounded-lg text-ink-200 font-medium' }, '⬇ Descargar CSV (fiscal)'),
  ]);
  content.appendChild(compareBox);
  apiClient.get('/api/v1/investments/vs-sp500').then((r) => {
    const slot = compareBox.querySelector('#vs-sp500-verdict');
    if (!slot) return;
    if (r?.error) { slot.textContent = '—'; return; }
    slot.textContent = r.verdict;
    slot.className = 'text-xs font-semibold ' + (r.verdict?.includes('BATE') ? 'text-green-300' : 'text-orange-300');
  }).catch(() => {});

  // Tabla holdings
  if (holdings.length === 0) {
    content.appendChild(h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-6 text-center' }, [
      h('p', { class: 'text-ink-300' }, 'Aún no tienes posiciones. Registra tu primera compra para empezar.'),
    ]));
    return;
  }

  const tbl = h('div', { class: 'space-y-2' }, holdings.map((h0) => {
    const pnlClass = (h0.unrealizedPnlEur || 0) >= 0 ? 'text-green-300' : 'text-red-300';
    return h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-3 flex items-center gap-3 flex-wrap' }, [
      h('div', { class: 'flex-1 min-w-[150px]' }, [
        h('div', { class: 'font-bold text-lg font-mono' }, h0.ticker),
        h('div', { class: 'text-xs text-ink-400' }, `${h0.shares.toFixed(4)} acc · ${h0.txCount} tx`),
      ]),
      h('div', { class: 'text-right' }, [
        h('div', { class: 'text-[10px] text-ink-400' }, 'Coste'),
        h('div', { class: 'text-sm' }, formatEur(h0.costBasisEur)),
      ]),
      h('div', { class: 'text-right' }, [
        h('div', { class: 'text-[10px] text-ink-400' }, 'Valor'),
        h('div', { class: 'text-sm font-semibold' }, h0.currentValueEur != null ? formatEur(h0.currentValueEur) : '…'),
      ]),
      h('div', { class: 'text-right' }, [
        h('div', { class: 'text-[10px] text-ink-400' }, 'P&L'),
        h('div', { class: `text-sm font-bold ${pnlClass}` }, h0.unrealizedPnlEur != null ? formatEur(h0.unrealizedPnlEur) : '—'),
        h('div', { class: `text-[10px] ${pnlClass}` }, fmtPct(h0.unrealizedPnlPct)),
      ]),
      h('button', { class: 'text-xs text-gold-400 hover:underline px-2', onclick: () => goAnalyze(h0.ticker) }, 'analizar SEC →'),
    ]);
  }));
  content.appendChild(tbl);
}

function goAnalyze(ticker) {
  // dispara cambio a tab analisis con ticker prellenado
  window.__INVEST_PREFILL_TICKER = ticker;
  const tabsBar = document.querySelector('.flex.gap-1.border-b.border-base-700');
  if (tabsBar) {
    const btns = [...tabsBar.children];
    const idx = btns.findIndex((b) => b.textContent.includes('Análisis'));
    if (idx >= 0) btns[idx].click();
  }
}

function openTxModal(onClose) {
  const overlay = h('div', { class: 'fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4' });
  const today = new Date().toISOString().slice(0, 10);
  const tickerIn = h('input', { type: 'text', placeholder: 'AAPL', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm uppercase' });
  const typeIn = h('select', { class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  for (const t of [['buy','Compra'],['sell','Venta'],['dividend','Dividendo']]) {
    typeIn.appendChild(h('option', { value: t[0] }, t[1]));
  }
  const sharesIn = h('input', { type: 'number', step: '0.0001', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  const priceIn = h('input', { type: 'number', step: '0.01', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  const currIn = h('input', { type: 'text', value: 'EUR', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm uppercase' });
  const feeIn = h('input', { type: 'number', step: '0.01', value: '1', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  const dateIn = h('input', { type: 'date', value: today, class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });

  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-5 w-full max-w-md' }, [
    h('h3', { class: 'font-display text-xl mb-3' }, 'Registrar operación'),
    h('div', { class: 'grid grid-cols-2 gap-3' }, [
      h('div', { class: 'col-span-2' }, [h('label', { class: 'text-[11px] text-ink-300' }, 'Ticker'), tickerIn]),
      h('div', {}, [h('label', { class: 'text-[11px] text-ink-300' }, 'Tipo'), typeIn]),
      h('div', {}, [h('label', { class: 'text-[11px] text-ink-300' }, 'Fecha'), dateIn]),
      h('div', {}, [h('label', { class: 'text-[11px] text-ink-300' }, 'Acciones'), sharesIn]),
      h('div', {}, [h('label', { class: 'text-[11px] text-ink-300' }, 'Precio/acción'), priceIn]),
      h('div', {}, [h('label', { class: 'text-[11px] text-ink-300' }, 'Moneda'), currIn]),
      h('div', {}, [h('label', { class: 'text-[11px] text-ink-300' }, 'Comisión €'), feeIn]),
    ]),
    h('div', { class: 'flex gap-2 mt-4 justify-end' }, [
      btn('Cancelar', () => overlay.remove(), { variant: 'secondary' }),
      btn('Guardar', async () => {
        try {
          await apiClient.post('/api/v1/investments/transactions', {
            ticker: tickerIn.value.trim(),
            txType: typeIn.value,
            shares: parseFloat(sharesIn.value) || 0,
            pricePerShare: parseFloat(priceIn.value) || 0,
            currency: currIn.value.trim() || 'EUR',
            feeEur: parseFloat(feeIn.value) || 0,
            executedAt: new Date(dateIn.value).toISOString(),
          });
          toast('Operación guardada', 'success');
          overlay.remove();
          onClose?.();
        } catch (e) {
          toast('Error: ' + (e.message || e), 'error');
        }
      }),
    ]),
  ]);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// ============================================================
// TAB 2 — WATCHLIST
// ============================================================
async function renderTabWatchlist(content) {
  const data = await apiClient.get('/api/v1/investments/watchlist');
  const list = data.watchlist || [];

  content.appendChild(h('div', { class: 'mb-3' }, [
    btn('+ Añadir ticker a watchlist', () => openWlModal(() => renderTabWatchlist(content)), { variant: 'primary' }),
  ]));

  if (list.length === 0) {
    content.appendChild(h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-6 text-center' }, [
      h('p', { class: 'text-ink-300' }, 'Watchlist vacía. Añade tickers que quieras vigilar sin tener (CSPX, NVDA, etc.).'),
    ]));
    return;
  }

  content.appendChild(h('div', { class: 'space-y-2' }, list.map((w) => h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-3 flex items-center gap-3 flex-wrap' }, [
    h('div', { class: 'flex-1 min-w-[150px]' }, [
      h('div', { class: 'font-bold text-lg font-mono' }, w.ticker),
      h('div', { class: 'text-xs text-ink-400' }, w.name || w.thesis || ''),
    ]),
    h('div', { class: 'text-right' }, [
      h('div', { class: 'text-[10px] text-ink-400' }, 'Precio'),
      h('div', { class: 'text-sm' }, w.lastPrice ? formatEur(w.lastPrice) : '…'),
    ]),
    w.targetPriceEur ? h('div', { class: 'text-right' }, [
      h('div', { class: 'text-[10px] text-ink-400' }, 'Objetivo'),
      h('div', { class: 'text-sm text-gold-300' }, formatEur(w.targetPriceEur)),
    ]) : null,
    h('button', { class: 'text-xs text-gold-400 hover:underline px-2', onclick: () => goAnalyze(w.ticker) }, 'analizar SEC →'),
    h('button', { class: 'text-xs text-red-400 hover:underline px-2', onclick: async () => {
      if (!confirm('¿Quitar ' + w.ticker + ' de la watchlist?')) return;
      await apiClient.delete('/api/v1/investments/watchlist/' + w.id);
      renderTabWatchlist(content);
    } }, '✕'),
  ].filter(Boolean)))));
}

function openWlModal(onClose) {
  const overlay = h('div', { class: 'fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4' });
  const tickerIn = h('input', { type: 'text', placeholder: 'NVDA', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm uppercase' });
  const nameIn = h('input', { type: 'text', placeholder: 'Nvidia', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  const thesisIn = h('textarea', { class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm', rows: '2', placeholder: 'Por qué la sigo…' });
  const targetIn = h('input', { type: 'number', step: '0.01', placeholder: '€ precio compra', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-5 w-full max-w-md' }, [
    h('h3', { class: 'font-display text-xl mb-3' }, 'Añadir a watchlist'),
    h('label', { class: 'text-[11px] text-ink-300' }, 'Ticker'), tickerIn,
    h('label', { class: 'text-[11px] text-ink-300 mt-2 block' }, 'Nombre'), nameIn,
    h('label', { class: 'text-[11px] text-ink-300 mt-2 block' }, 'Tesis (opcional)'), thesisIn,
    h('label', { class: 'text-[11px] text-ink-300 mt-2 block' }, 'Precio objetivo €'), targetIn,
    h('div', { class: 'flex gap-2 mt-4 justify-end' }, [
      btn('Cancelar', () => overlay.remove(), { variant: 'secondary' }),
      btn('Añadir', async () => {
        try {
          await apiClient.post('/api/v1/investments/watchlist', {
            ticker: tickerIn.value.trim(),
            name: nameIn.value.trim() || undefined,
            thesis: thesisIn.value.trim() || undefined,
            targetPriceEur: parseFloat(targetIn.value) || undefined,
          });
          toast('Añadido a watchlist', 'success');
          overlay.remove();
          onClose?.();
        } catch (e) { toast('Error: ' + (e.message || e), 'error'); }
      }),
    ]),
  ]);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// ============================================================
// TAB 3 — ANÁLISIS SEC
// ============================================================
async function renderTabAnalisis(content) {
  const prefill = window.__INVEST_PREFILL_TICKER || 'AAPL';
  window.__INVEST_PREFILL_TICKER = null;
  await renderSecExplorer(content, prefill);
}


// ============================================================
// TAB 4 — PLAN DE AHORRO
// ============================================================
async function renderTabPlan(content) {
  const data = await apiClient.get('/api/v1/investments/savings-plans');
  const plans = data.plans || [];

  content.appendChild(h('div', { class: 'bg-blue-500/5 border border-blue-500/30 rounded-lg p-3 mb-3' }, [
    h('p', { class: 'text-sm' }, [
      h('strong', { class: 'text-blue-300' }, '🔁 Trade Republic Sparplan — '),
      'planes de ahorro automáticos. Cero comisión. Registra aquí los que tienes activos para hacer seguimiento.',
    ]),
  ]));

  content.appendChild(h('div', { class: 'mb-3' }, [
    btn('+ Crear plan de ahorro', () => openPlanModal(() => renderTabPlan(content)), { variant: 'primary' }),
  ]));

  if (plans.length === 0) {
    content.appendChild(h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-6 text-center' }, [
      h('p', { class: 'text-ink-300' }, 'Sin planes activos. Crea uno cuando configures tu Sparplan en Trade Republic.'),
    ]));
    return;
  }

  content.appendChild(h('div', { class: 'space-y-2' }, plans.map((p) => {
    const proj = projection(p);
    return h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-4' }, [
      h('div', { class: 'flex items-center justify-between gap-3 flex-wrap mb-2' }, [
        h('div', {}, [
          h('div', { class: 'font-bold text-lg font-mono' }, p.ticker),
          h('div', { class: 'text-xs text-ink-400' }, `${p.name || ''} · ${p.frequency} · ${formatEur(p.amountEur)}/aportación`),
        ]),
        h('div', { class: 'text-right' }, [
          h('div', { class: 'text-[10px] text-ink-400' }, 'Aportado total'),
          h('div', { class: 'text-lg font-bold text-gold-300' }, formatEur(p.totalContributedEur || 0)),
          h('div', { class: 'text-[10px] text-ink-400' }, `${p.contributionCount || 0} aportaciones`),
        ]),
      ]),
      h('div', { class: 'grid grid-cols-3 gap-2 text-xs mt-2' }, [
        h('div', { class: 'bg-base-900/50 border border-base-700 rounded px-2 py-1' }, [
          h('div', { class: 'text-[10px] text-ink-400' }, 'Proyección 5 años (8%)'),
          h('div', { class: 'font-bold text-green-300' }, formatEur(proj.y5)),
        ]),
        h('div', { class: 'bg-base-900/50 border border-base-700 rounded px-2 py-1' }, [
          h('div', { class: 'text-[10px] text-ink-400' }, 'Proyección 10 años'),
          h('div', { class: 'font-bold text-green-300' }, formatEur(proj.y10)),
        ]),
        h('div', { class: 'bg-base-900/50 border border-base-700 rounded px-2 py-1' }, [
          h('div', { class: 'text-[10px] text-ink-400' }, 'Proyección 20 años'),
          h('div', { class: 'font-bold text-green-300' }, formatEur(proj.y20)),
        ]),
      ]),
      h('div', { class: 'flex gap-2 mt-3' }, [
        btn('+ Aportación', async () => {
          await apiClient.post(`/api/v1/investments/savings-plans/${p.id}/contribution`, {});
          toast('Aportación registrada', 'success');
          renderTabPlan(content);
        }, { variant: 'primary' }),
        btn(p.isActive ? 'Pausar' : 'Reanudar', async () => {
          await apiClient.put(`/api/v1/investments/savings-plans/${p.id}`, { isActive: !p.isActive });
          renderTabPlan(content);
        }, { variant: 'secondary' }),
        btn('Eliminar', async () => {
          if (!confirm('¿Eliminar plan?')) return;
          await apiClient.delete(`/api/v1/investments/savings-plans/${p.id}`);
          renderTabPlan(content);
        }, { variant: 'secondary' }),
      ]),
    ]);
  })));
}

function projection(p) {
  const annualReturn = 0.08;
  const perYear = p.frequency === 'monthly' ? 12 : p.frequency === 'biweekly' ? 26 : 52;
  const r = annualReturn / perYear;
  const c = p.amountEur;
  const calc = (years) => {
    const n = years * perYear;
    if (r === 0) return c * n;
    return c * ((Math.pow(1 + r, n) - 1) / r);
  };
  return { y5: calc(5), y10: calc(10), y20: calc(20) };
}

function openPlanModal(onClose) {
  const overlay = h('div', { class: 'fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4' });
  const today = new Date().toISOString().slice(0, 10);
  const tickerIn = h('input', { type: 'text', placeholder: 'CSPX', value: 'CSPX', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm uppercase' });
  const nameIn = h('input', { type: 'text', placeholder: 'S&P 500', value: 'S&P 500', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  const amountIn = h('input', { type: 'number', step: '10', value: '300', class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  const freqIn = h('select', { class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });
  for (const f of [['monthly','Mensual'],['biweekly','Quincenal'],['weekly','Semanal']]) {
    freqIn.appendChild(h('option', { value: f[0] }, f[1]));
  }
  const startIn = h('input', { type: 'date', value: today, class: 'w-full bg-base-900 border border-base-600 rounded px-2 py-1.5 text-sm' });

  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-5 w-full max-w-md' }, [
    h('h3', { class: 'font-display text-xl mb-3' }, 'Nuevo plan de ahorro'),
    h('div', { class: 'space-y-2' }, [
      h('label', { class: 'text-[11px] text-ink-300' }, 'Ticker'), tickerIn,
      h('label', { class: 'text-[11px] text-ink-300' }, 'Nombre'), nameIn,
      h('label', { class: 'text-[11px] text-ink-300' }, 'Importe por aportación (€)'), amountIn,
      h('label', { class: 'text-[11px] text-ink-300' }, 'Frecuencia'), freqIn,
      h('label', { class: 'text-[11px] text-ink-300' }, 'Inicio'), startIn,
    ]),
    h('div', { class: 'flex gap-2 mt-4 justify-end' }, [
      btn('Cancelar', () => overlay.remove(), { variant: 'secondary' }),
      btn('Crear', async () => {
        try {
          await apiClient.post('/api/v1/investments/savings-plans', {
            ticker: tickerIn.value.trim(),
            name: nameIn.value.trim() || undefined,
            amountEur: parseFloat(amountIn.value) || 0,
            frequency: freqIn.value,
            startedAt: new Date(startIn.value).toISOString(),
          });
          toast('Plan creado', 'success');
          overlay.remove();
          onClose?.();
        } catch (e) { toast('Error: ' + (e.message || e), 'error'); }
      }),
    ]),
  ]);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// ============================================================
// TAB 5 — ALERTAS
// ============================================================
async function renderTabAlertas(content) {
  const r = await apiClient.get('/api/v1/investments/alerts-config');
  const c = r.config || {};

  content.appendChild(h('div', { class: 'bg-blue-500/5 border border-blue-500/30 rounded-lg p-3 mb-3' }, [
    h('p', { class: 'text-sm' }, [
      h('strong', { class: 'text-blue-300' }, '🔔 Alertas Telegram automáticas — '),
      'el sistema revisa cada hora los tickers de tu cartera + watchlist y te avisa cuando hay movimientos importantes.',
    ]),
  ]));

  const toggles = [
    { key: 'alertInsiderSellMassive', icon: '⚠️',  label: 'Venta masiva insiders', desc: '3+ directivos vendiendo en 7 días' },
    { key: 'alertInsiderBuyBig',       icon: '🟢', label: 'Compra grande insider',  desc: 'Directivo compra >100k$ con dinero propio' },
    { key: 'alertEarningsPublished',   icon: '📊', label: '10-Q publicado',         desc: 'Resumen automático cuando salen resultados' },
    { key: 'alert8kImportant',         icon: '📰', label: '8-K importante',         desc: 'CEO change, adquisiciones, dividendos…' },
    { key: 'alertBuffettActivist',     icon: '🏛️', label: 'Buffett/Activist entra', desc: 'Berkshire, Icahn, Ackman declaran posición' },
  ];

  const out = h('div', { class: 'space-y-2' });
  for (const t of toggles) {
    const checked = !!c[t.key];
    const row = h('label', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:bg-base-900/70' });
    const cb = h('input', { type: 'checkbox', class: 'w-5 h-5' });
    cb.checked = checked;
    cb.addEventListener('change', async () => {
      try {
        await apiClient.put('/api/v1/investments/alerts-config', { [t.key]: cb.checked });
        toast('Guardado', 'success');
      } catch (e) { toast('Error: ' + (e.message || e), 'error'); cb.checked = !cb.checked; }
    });
    row.append(cb, h('div', {}, [
      h('div', { class: 'font-semibold text-sm' }, `${t.icon} ${t.label}`),
      h('div', { class: 'text-xs text-ink-400' }, t.desc),
    ]));
    out.appendChild(row);
  }
  content.appendChild(out);
}


// ============================================================
// TAB 6 — EARNINGS CALENDAR
// ============================================================
async function renderTabEarnings(content) {
  content.appendChild(h('div', { class: 'bg-blue-500/5 border border-blue-500/30 rounded-lg p-3 mb-3' }, [
    h('p', { class: 'text-sm' }, [
      h('strong', { class: 'text-blue-300' }, '📅 Próximos earnings — '),
      'fechas estimadas de cuándo publicarán resultados las empresas de tu cartera y watchlist. Útil para no perderte las noticias importantes.',
    ]),
  ]));

  const out = h('div', { class: 'text-ink-400 text-sm py-8 text-center' }, 'Calculando próximas fechas...');
  content.appendChild(out);
  try {
    const r = await apiClient.get('/api/v1/investments/earnings-calendar');
    const upcoming = r.upcoming || [];
    out.innerHTML = '';
    if (upcoming.length === 0) {
      out.appendChild(h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-6 text-center' }, [
        h('p', { class: 'text-ink-300' }, 'Sin tickers en cartera ni watchlist. Añade alguno para ver las próximas fechas.'),
      ]));
      return;
    }
    const now = new Date();
    out.appendChild(h('div', { class: 'space-y-2' }, upcoming.map((u) => {
      const next = u.estimatedNextEarnings ? new Date(u.estimatedNextEarnings) : null;
      const daysUntil = next ? Math.round((next - now) / (1000*60*60*24)) : null;
      let urgency, urgencyColor;
      if (daysUntil == null)        { urgency = '—';            urgencyColor = 'text-ink-400'; }
      else if (daysUntil < 0)       { urgency = 'Ya publicado'; urgencyColor = 'text-green-300'; }
      else if (daysUntil < 7)       { urgency = `En ${daysUntil}d`; urgencyColor = 'text-red-300 font-bold'; }
      else if (daysUntil < 30)      { urgency = `En ${daysUntil}d`; urgencyColor = 'text-orange-300'; }
      else                          { urgency = `En ${daysUntil}d`; urgencyColor = 'text-ink-300'; }
      return h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-3 flex items-center gap-3 flex-wrap' }, [
        h('span', { class: 'text-2xl' }, '📊'),
        h('div', { class: 'flex-1 min-w-[120px]' }, [
          h('div', { class: 'font-bold font-mono' }, u.ticker),
          h('div', { class: 'text-xs text-ink-400' }, `Último 10-Q: ${u.lastReportPeriod || '—'}`),
        ]),
        h('div', { class: 'text-right' }, [
          h('div', { class: 'text-[10px] text-ink-400 uppercase' }, 'Próximos earnings'),
          h('div', { class: 'text-sm' }, u.estimatedNextEarnings || '—'),
        ]),
        h('div', { class: `text-sm ${urgencyColor}` }, urgency),
      ]);
    })));
    out.appendChild(h('p', { class: 'text-[10px] text-ink-500 text-center mt-3' }, 'Estimación basada en cadencia trimestral. La fecha exacta la confirma la empresa por 8-K ~2 semanas antes.'));
  } catch (e) {
    out.innerHTML = '';
    out.appendChild(h('p', { class: 'text-red-400 text-sm' }, 'Error: ' + (e.message || e)));
  }
}

// ============================================================
// TAB — ORO & ALTERNATIVOS (Glint, BullionVault, Tavex, custodia propia, arte, relojes, vino)
// ============================================================
const ALT_CATEGORIES = [
  { key: 'gold',     label: 'Oro físico',    icon: '🥇', color: 'gold' },
  { key: 'silver',   label: 'Plata física',  icon: '🥈', color: 'sky' },
  { key: 'platinum', label: 'Platino',       icon: '⚪', color: 'violet' },
  { key: 'art',      label: 'Arte',          icon: '🎨', color: 'rose' },
  { key: 'watch',    label: 'Relojes',       icon: '⌚', color: 'amber' },
  { key: 'wine',     label: 'Vino',          icon: '🍷', color: 'red' },
  { key: 'collect',  label: 'Coleccionables', icon: '💎', color: 'emerald' },
  { key: 'other',    label: 'Otros',         icon: '📦', color: 'ink' },
];
const ALT_PLATFORMS = ['Glint', 'BullionVault', 'Tavex', 'Andorrano', 'Custodia propia', 'Vault', 'Caja fuerte', 'Banco', 'Otro'];

async function renderTabAltAssets(content) {
  content.innerHTML = '';
  content.appendChild(h('div', { class: 'text-ink-300 text-sm py-3' }, 'Cargando activos alternativos…'));

  let data;
  try {
    data = await apiClient.get('/api/v1/alt-assets');
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(h('p', { class: 'text-red-400 text-sm' }, 'Error: ' + (e.message || e)));
    return;
  }
  content.innerHTML = '';

  const { items = [], kpis = {}, byCategory = {} } = data;

  // KPIs
  const kpiGrid = h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-5' }, [
    h('div', { class: 'bg-base-800/60 border border-base-700 rounded-lg p-3' }, [
      h('div', { class: 'text-xs text-ink-400' }, 'Items totales'),
      h('div', { class: 'text-xl font-bold text-ink-100' }, String(kpis.totalCount || 0)),
    ]),
    h('div', { class: 'bg-base-800/60 border border-base-700 rounded-lg p-3' }, [
      h('div', { class: 'text-xs text-ink-400' }, 'Coste base'),
      h('div', { class: 'text-xl font-bold text-ink-100' }, formatEur(kpis.totalCostEur || 0)),
    ]),
    h('div', { class: 'bg-base-800/60 border border-base-700 rounded-lg p-3' }, [
      h('div', { class: 'text-xs text-ink-400' }, 'Valor actual'),
      h('div', { class: 'text-xl font-bold text-gold-300' }, formatEur(kpis.totalValueEur || 0)),
    ]),
    h('div', { class: 'bg-base-800/60 border border-base-700 rounded-lg p-3' }, [
      h('div', { class: 'text-xs text-ink-400' }, 'P&L no realizada'),
      h('div', { class: 'text-xl font-bold ' + ((kpis.unrealizedPnlEur || 0) >= 0 ? 'text-green-300' : 'text-red-300') },
        formatEur(kpis.unrealizedPnlEur || 0) + ' (' + (kpis.unrealizedPnlPct || 0).toFixed(1) + '%)'),
    ]),
  ]);
  content.appendChild(kpiGrid);

  // Header con CTA
  content.appendChild(h('div', { class: 'flex items-center justify-between mb-4 flex-wrap gap-2' }, [
    h('div', {}, [
      h('h3', { class: 'font-semibold text-ink-100' }, 'Activos físicos y alternativos'),
      h('p', { class: 'text-xs text-ink-400' }, 'Oro/plata custodiados (Glint, BullionVault…), arte, relojes, vino y otros tangibles.'),
    ]),
    h('div', { class: 'flex gap-2 flex-wrap' }, [
      h('button', {
        class: 'px-3 py-1.5 text-xs bg-base-800 border border-base-700 rounded hover:bg-base-700',
        onclick: async () => {
          const res = await apiClient.get('/api/v1/alt-assets/spot');
          alert('Spot tickers disponibles:\n' + (res.tickers || []).map(t => `${t.symbol} (${t.yahooTicker}) — ${t.name}`).join('\n'));
        },
      }, '📈 Spot precios'),
      h('button', {
        class: 'px-3 py-1.5 text-xs bg-gold-500/20 border border-gold-500/40 text-gold-200 rounded hover:bg-gold-500/30',
        onclick: () => openAltAssetModal(null, () => renderTabAltAssets(content)),
      }, '+ Añadir activo'),
    ]),
  ]));

  if (items.length === 0) {
    content.appendChild(h('div', { class: 'bg-base-800/40 border border-base-700 rounded-lg p-8 text-center' }, [
      h('div', { class: 'text-4xl mb-2' }, '🥇'),
      h('h4', { class: 'font-semibold text-ink-100 mb-1' }, 'Sin activos alternativos'),
      h('p', { class: 'text-sm text-ink-400 mb-3' }, 'Añade tus tenencias en Glint, BullionVault o cualquier oro/plata físico custodiado.'),
      h('button', {
        class: 'px-4 py-2 text-sm bg-gold-500/20 border border-gold-500/40 text-gold-200 rounded',
        onclick: () => openAltAssetModal(null, () => renderTabAltAssets(content)),
      }, '+ Añadir primer activo'),
    ]));
    return;
  }

  // Agrupar por categoría
  const grouped = {};
  for (const it of items) {
    const k = it.category || 'other';
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(it);
  }

  const groupsEl = h('div', { class: 'space-y-3' });
  for (const cat of ALT_CATEGORIES) {
    const list = grouped[cat.key] || [];
    if (list.length === 0) continue;
    const totalValue = list.reduce((s, it) => s + (Number(it.current_value_eur) || 0), 0);
    const totalCost = list.reduce((s, it) => s + (Number(it.cost_basis_eur) || 0), 0);
    const pnl = totalValue - totalCost;

    groupsEl.appendChild(h('div', { class: 'bg-base-800/40 border border-base-700 rounded-lg overflow-hidden' }, [
      h('div', { class: 'flex items-center justify-between px-4 py-2.5 bg-base-800/60 border-b border-base-700' }, [
        h('div', { class: 'flex items-center gap-2' }, [
          h('span', { class: 'text-xl' }, cat.icon),
          h('span', { class: 'font-semibold text-sm' }, cat.label),
          h('span', { class: 'text-xs text-ink-400' }, `${list.length} items`),
        ]),
        h('div', { class: 'text-xs flex items-center gap-3' }, [
          h('span', { class: 'text-ink-300' }, 'Coste: ' + formatEur(totalCost)),
          h('span', { class: 'text-gold-300 font-semibold' }, 'Valor: ' + formatEur(totalValue)),
          h('span', { class: pnl >= 0 ? 'text-green-300' : 'text-red-300' }, (pnl >= 0 ? '+' : '') + formatEur(pnl)),
        ]),
      ]),
      h('div', { class: 'divide-y divide-base-800' }, list.map((it) => altRow(it, () => renderTabAltAssets(content)))),
    ]));
  }
  content.appendChild(groupsEl);
}

function altRow(it, reload) {
  const pnl = (Number(it.current_value_eur) || 0) - (Number(it.cost_basis_eur) || 0);
  return h('div', { class: 'flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm' }, [
    h('div', { class: 'flex-1 min-w-[160px]' }, [
      h('div', { class: 'font-medium' }, it.asset_name),
      h('div', { class: 'text-xs text-ink-400 flex items-center gap-2' }, [
        it.platform ? h('span', { class: 'px-1.5 py-0.5 bg-base-700 rounded' }, '🏛️ ' + it.platform) : null,
        h('span', {}, `${it.quantity} ${it.unit || ''}`),
        it.purchase_date ? h('span', {}, '📅 ' + formatDate(it.purchase_date)) : null,
      ].filter(Boolean)),
    ]),
    h('div', { class: 'text-xs text-right' }, [
      it.cost_basis_eur != null ? h('div', { class: 'text-ink-300' }, 'Coste: ' + formatEur(it.cost_basis_eur)) : null,
      it.current_value_eur != null ? h('div', { class: 'text-gold-300 font-semibold' }, 'Valor: ' + formatEur(it.current_value_eur)) : null,
      (it.cost_basis_eur != null && it.current_value_eur != null) ? h('div', { class: pnl >= 0 ? 'text-green-300' : 'text-red-300' }, (pnl >= 0 ? '+' : '') + formatEur(pnl)) : null,
    ].filter(Boolean)),
    h('div', { class: 'flex items-center gap-1' }, [
      h('button', {
        class: 'text-xs px-2 py-1 bg-base-800 hover:bg-base-700 rounded',
        onclick: () => openAltAssetModal(it, reload),
      }, '✏️'),
      h('button', {
        class: 'text-xs px-2 py-1 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 rounded',
        onclick: async () => {
          if (!confirm(`¿Eliminar "${it.asset_name}"?`)) return;
          await apiClient.delete(`/api/v1/alt-assets/${it.id}`);
          reload();
        },
      }, '🗑️'),
    ]),
  ]);
}

function openAltAssetModal(existing, reload) {
  const backdrop = h('div', {
    class: 'fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === backdrop) backdrop.remove(); },
  });
  const isEdit = !!existing;
  const state = {
    category:        existing?.category || 'gold',
    platform:        existing?.platform || 'Glint',
    assetName:       existing?.asset_name || '',
    quantity:        existing?.quantity ?? 0,
    unit:            existing?.unit || 'g',
    costBasisEur:    existing?.cost_basis_eur ?? null,
    currentValueEur: existing?.current_value_eur ?? null,
    purchaseDate:    existing?.purchase_date ? new Date(existing.purchase_date).toISOString().split('T')[0] : '',
    notes:           existing?.notes || '',
  };

  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-5 max-w-md w-full max-h-[85vh] overflow-y-auto' }, [
    h('div', { class: 'flex items-center justify-between mb-4' }, [
      h('h3', { class: 'text-lg font-bold' }, isEdit ? '✏️ Editar activo' : '+ Nuevo activo alternativo'),
      h('button', { class: 'text-ink-400 hover:text-white text-2xl leading-none', onclick: () => backdrop.remove() }, '×'),
    ]),
    h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Categoría'),
    h('select', {
      class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2 mb-3',
      onchange: (e) => { state.category = e.target.value; },
    }, ALT_CATEGORIES.map((c) => {
      const o = { value: c.key };
      if (c.key === state.category) o.selected = 'selected';
      return h('option', o, `${c.icon} ${c.label}`);
    })),
    h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Plataforma / Custodio'),
    h('select', {
      class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2 mb-3',
      onchange: (e) => { state.platform = e.target.value; },
    }, ALT_PLATFORMS.map((p) => {
      const o = { value: p };
      if (p === state.platform) o.selected = 'selected';
      return h('option', o, p);
    })),
    h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Nombre / descripción *'),
    h('input', {
      type: 'text',
      class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2 mb-3',
      value: state.assetName,
      placeholder: 'p.ej. Oro 999 Glint · Lingote 100g Tavex · Rolex Submariner',
      oninput: (e) => { state.assetName = e.target.value; },
    }),
    h('div', { class: 'grid grid-cols-2 gap-3 mb-3' }, [
      h('div', {}, [
        h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Cantidad'),
        h('input', {
          type: 'number', step: '0.001', min: '0',
          class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2',
          value: String(state.quantity || ''),
          oninput: (e) => { state.quantity = Number(e.target.value) || 0; },
        }),
      ]),
      h('div', {}, [
        h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Unidad'),
        h('select', {
          class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2',
          onchange: (e) => { state.unit = e.target.value; },
        }, ['g', 'oz', 'kg', 'ud', 'L', 'cl'].map((u) => {
          const o = { value: u };
          if (u === state.unit) o.selected = 'selected';
          return h('option', o, u);
        })),
      ]),
    ]),
    h('div', { class: 'grid grid-cols-2 gap-3 mb-3' }, [
      h('div', {}, [
        h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Coste base (€)'),
        h('input', {
          type: 'number', step: '0.01', min: '0',
          class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2',
          value: state.costBasisEur != null ? String(state.costBasisEur) : '',
          oninput: (e) => { state.costBasisEur = e.target.value ? Number(e.target.value) : null; },
        }),
      ]),
      h('div', {}, [
        h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Valor actual (€)'),
        h('input', {
          type: 'number', step: '0.01', min: '0',
          class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2',
          value: state.currentValueEur != null ? String(state.currentValueEur) : '',
          oninput: (e) => { state.currentValueEur = e.target.value ? Number(e.target.value) : null; },
        }),
      ]),
    ]),
    h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Fecha de compra'),
    h('input', {
      type: 'date',
      class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2 mb-3',
      value: state.purchaseDate,
      oninput: (e) => { state.purchaseDate = e.target.value; },
    }),
    h('label', { class: 'block text-xs text-ink-400 mb-1' }, 'Notas'),
    h('textarea', {
      class: 'w-full bg-base-800 border border-base-700 rounded px-3 py-2 mb-3',
      rows: 2,
      placeholder: 'p.ej. nº serie, pureza, certificado, seguro…',
      oninput: (e) => { state.notes = e.target.value; },
    }, state.notes),
    h('div', { class: 'flex gap-2' }, [
      h('button', {
        class: 'flex-1 px-3 py-2 text-sm bg-gold-500/20 border border-gold-500/40 text-gold-200 rounded hover:bg-gold-500/30',
        onclick: async () => {
          if (!state.assetName.trim()) { alert('Falta nombre'); return; }
          if (!state.quantity || state.quantity <= 0) { alert('Cantidad inválida'); return; }
          const body = {
            category: state.category,
            platform: state.platform || null,
            assetName: state.assetName.trim(),
            quantity: state.quantity,
            unit: state.unit,
            costBasisEur: state.costBasisEur,
            currentValueEur: state.currentValueEur,
            purchaseDate: state.purchaseDate ? new Date(state.purchaseDate).getTime() : null,
            notes: state.notes || null,
          };
          try {
            if (isEdit) {
              await apiClient.put(`/api/v1/alt-assets/${existing.id}`, body);
            } else {
              await apiClient.post('/api/v1/alt-assets', body);
            }
            backdrop.remove();
            reload();
          } catch (e) {
            alert('Error: ' + (e.message || e));
          }
        },
      }, isEdit ? '💾 Guardar' : '+ Crear'),
      h('button', {
        class: 'px-3 py-2 text-sm bg-base-800 border border-base-700 rounded hover:bg-base-700',
        onclick: () => backdrop.remove(),
      }, 'Cancelar'),
    ]),
  ]);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
}
